// Copyright 2026 Hardfin, Inc.
// Hardfin Confidential - Restricted
// Visual stepper component for the company data upload workflow.
// Displays the current progress through the three-step upload process.
import { Check } from '@/constants/hardfin-standard-icons';
import type { Step } from '@/customer/components/company-data-upload/types';
import { Box, Stack, Typography } from '@mui/material';

interface Props {
  currentStep: Step;
}

const steps: { key: Step; label: string }[] = [
  { key: 'download-template', label: 'Download template' },
  { key: 'upload-csv', label: 'Upload CSV' },
  { key: 'results', label: 'Results' },
];

/**
 * Renders a stepper component to visualize the current step in the upload process.
 */
export function Stepper(props: Props) {
  const currentStepIndex = steps.findIndex(
    (step) => step.key === props.currentStep
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            zIndex: 0,
          },
        }}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;

          return (
            <Box
              key={step.key}
              sx={{
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'white',
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  backgroundColor: isCompleted
                    ? 'success.main'
                    : isCurrent
                      ? 'primary.main'
                      : 'background.paper',
                  border: (theme) =>
                    `2px solid ${
                      isCompleted
                        ? theme.palette.success.main
                        : isCurrent
                          ? theme.palette.primary.main
                          : theme.palette.divider
                    }`,
                  color: isCompleted || isCurrent ? 'white' : 'text.secondary',
                  mb: 1,
                }}
              >
                {isCompleted ? <Check /> : index + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: isCurrent ? 'bold' : 'normal',
                  color: isCurrent ? 'primary.main' : 'text.secondary',
                  textAlign: 'center',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
